import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Paper,
    Typography,
    Chip,
    Button,
    TextField,
    Alert,
    CircularProgress,
    Divider,
    Stack,
} from "@mui/material";
import {
    PlayArrow as StartIcon,
    Send as SubmitIcon,
    CheckCircle as ApproveIcon,
    Undo as ReopenIcon,
    Cancel as RejectIcon,
    AccessTime as ClockIcon,
} from "@mui/icons-material";
import WorkOrderService from "../services/WorkOrder.service";
import { TRAVELER_STATUS_META } from "../components/Shop/WorkOrders/statusMeta";

export default function TravelerScan() {
    const { token } = useParams();
    const navigate = useNavigate();

    const [traveler, setTraveler] = useState(null);
    const [workOrder, setWorkOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState("");
    const [rejecting, setRejecting] = useState(false);
    const [rejectNotes, setRejectNotes] = useState("");

    const refresh = useCallback(async () => {
        setLoading(true);
        setNotFound(false);
        setError("");
        try {
            const res = await WorkOrderService.getTravelerByToken(token);
            setTraveler(res.data);
            try {
                const woRes = await WorkOrderService.getWorkOrder(res.data.manufacture_order);
                setWorkOrder(woRes.data);
            } catch {
                setWorkOrder(null);
            }
        } catch (err) {
            if (err.response?.status === 404) setNotFound(true);
            else setError("Failed to load this traveler.");
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const runAction = async (fn) => {
        setActionLoading(true);
        setError("");
        try {
            await fn();
            await refresh();
        } catch (err) {
            const detail =
                err.response?.data?.detail ||
                err.response?.data?.non_field_errors?.[0] ||
                "That action could not be completed.";
            setError(detail);
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = () => {
        runAction(async () => {
            await WorkOrderService.failInspectionTraveler(traveler.id, rejectNotes);
            setRejecting(false);
            setRejectNotes("");
        });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
            </Box>
        );
    }

    if (notFound) {
        return (
            <Box p={2}>
                <Alert severity="error">
                    This QR code doesn't match any traveler. It may have been reprinted
                    under a new work order.
                </Alert>
            </Box>
        );
    }

    if (!traveler) {
        return (
            <Box p={2}>
                <Alert severity="error">{error || "Something went wrong."}</Alert>
            </Box>
        );
    }

    const meta = TRAVELER_STATUS_META[traveler.status] || {
        label: traveler.status,
        color: "default",
    };

    return (
        <Box sx={{ p: 2, maxWidth: 520, mx: "auto" }}>
            <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: "divider", borderRadius: 2 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="overline" color="text.secondary">
                            {traveler.traveler_number}
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                            {traveler.step_number}. {traveler.step_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {traveler.work_center_name}
                        </Typography>
                        {traveler.parallel_with_previous && (
                            <Typography variant="caption" color="success.main" display="block">
                                Runs at the same time as the step before it
                            </Typography>
                        )}
                    </Box>
                    <Chip label={meta.label} color={meta.color} size="small" />
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="subtitle2" fontWeight={700}>
                    {traveler.work_order_number}
                </Typography>
                {workOrder && (
                    <>
                        <Typography variant="body2" color="text.secondary">
                            {workOrder.part_display} — {workOrder.part_description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Qty {workOrder.quantity}
                        </Typography>
                    </>
                )}

                {traveler.instructions && (
                    <>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Instructions
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {traveler.instructions}
                        </Typography>
                    </>
                )}

                <Divider sx={{ my: 1.5 }} />

                {error && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                        {error}
                    </Alert>
                )}

                <Stack spacing={1}>
                    {traveler.status === "pending" && (
                        <Button
                            variant="contained"
                            startIcon={<StartIcon />}
                            disabled={actionLoading || !traveler.can_start}
                            onClick={() => runAction(() => WorkOrderService.startTraveler(traveler.id))}
                        >
                            Start Work
                        </Button>
                    )}
                    {traveler.status === "pending" && !traveler.can_start && (
                        <Typography variant="caption" color="text.secondary">
                            Waiting on an earlier step to be approved before this one can start.
                        </Typography>
                    )}

                    {traveler.status === "in_progress" && (
                        <>
                            {/* Hours go through the payroll timesheet (which also feeds this
                                step's actual hours), never straight onto the traveler. */}
                            <Button
                                variant="outlined"
                                startIcon={<ClockIcon />}
                                onClick={() => navigate(`/log-time/${token}`)}
                            >
                                Log My Hours
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<SubmitIcon />}
                                disabled={actionLoading}
                                onClick={() => runAction(() => WorkOrderService.submitTraveler(traveler.id))}
                            >
                                Submit for Inspection
                            </Button>
                        </>
                    )}

                    {(traveler.status === "in_progress" || traveler.status === "submitted") && (
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<ApproveIcon />}
                            disabled={actionLoading}
                            onClick={() => runAction(() => WorkOrderService.completeTraveler(traveler.id))}
                        >
                            Approve
                        </Button>
                    )}

                    {traveler.status === "submitted" && !rejecting && (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<RejectIcon />}
                            disabled={actionLoading}
                            onClick={() => setRejecting(true)}
                        >
                            Send Back for Rework
                        </Button>
                    )}
                    {rejecting && (
                        <Stack spacing={1}>
                            <TextField
                                label="Reason"
                                size="small"
                                multiline
                                minRows={2}
                                value={rejectNotes}
                                onChange={(e) => setRejectNotes(e.target.value)}
                            />
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    disabled={actionLoading}
                                    onClick={handleReject}
                                >
                                    Confirm
                                </Button>
                                <Button disabled={actionLoading} onClick={() => setRejecting(false)}>
                                    Cancel
                                </Button>
                            </Stack>
                        </Stack>
                    )}

                    {traveler.status === "approved" && (
                        <>
                            <Alert severity="success" icon={<ApproveIcon fontSize="inherit" />}>
                                This step has been approved.
                                {traveler.approved_at && (
                                    <>
                                        <br />
                                        Approved by {traveler.completed_by_name || "Unknown user"} on{" "}
                                        {new Date(traveler.approved_at).toLocaleString()}
                                    </>
                                )}
                            </Alert>
                            <Button
                                variant="text"
                                startIcon={<ReopenIcon />}
                                disabled={actionLoading}
                                onClick={() => runAction(() => WorkOrderService.unapproveTraveler(traveler.id))}
                            >
                                Reopen for Correction
                            </Button>
                        </>
                    )}
                </Stack>

                <Divider sx={{ my: 1.5 }} />
                <Button size="small" onClick={() => navigate("/shop/work-orders")}>
                    View Full Work Order
                </Button>
            </Paper>
        </Box>
    );
}
